import React, { useEffect, useRef, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { manageDropdownListener } from '@helpers/use_effect_callbacks';

import './styles/main.scss';

interface DropdownWrapperProps {
  children: JSX.Element;
  dropdownContents: JSX.Element;
  close?: boolean;
  closeCallback?: () => void;
  cssTransitionClasses?: string;
  openOnClick?: boolean;
  openOnHover?: boolean;
  wrapperClassnames?: string;
}

const DropdownWrapper = ({
  children,
  dropdownContents,
  close = false,
  closeCallback = undefined,
  cssTransitionClasses = '',
  openOnClick = true,
  openOnHover = true,
  wrapperClassnames = '',
}: DropdownWrapperProps): JSX.Element => {
  const [showDropdown, setShowDropdown] = useState(false);
  const menuRef = useRef(null);

  const { device } = BFG.currentUser;
  const isTouchScreen = device === 'mobile-view' || device === 'tablet-view';

  const handleOpenAndClose = (e: any): void => {
    switch (e.type) {
      case 'keypress':
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') setShowDropdown((prevState) => !prevState);
        break;
      case 'click':
        if (openOnClick || isTouchScreen) setShowDropdown((prevState) => !prevState);
        break;
      case 'mouseenter':
      case 'mouseleave':
        if (openOnHover && !isTouchScreen) setShowDropdown(e.type === 'mouseenter');
        break;
      default:
    }
  };

  const dropdownEventListenerCallback = (e: MouseEvent | KeyboardEvent): void => {
    e.stopPropagation();
    setShowDropdown(false);
  };

  useEffect(() => setShowDropdown(false), []); // close dropdown when unmounting

  useEffect(() => (
    manageDropdownListener(menuRef, showDropdown, dropdownEventListenerCallback)
  ), [menuRef, showDropdown, dropdownEventListenerCallback]);

  useEffect(() => {
    if (close) {
      setShowDropdown(false); // allows consumer/parent component to close dropdown
      if (closeCallback) {
        closeCallback();
      }
    }
  }, [close]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div
      ref={menuRef}
      className={`bf-dropdown-wrapper ${wrapperClassnames} ${showDropdown === true ? 'open' : ''}`}
      onMouseEnter={handleOpenAndClose}
      onMouseLeave={handleOpenAndClose}
    >
      <div
        className="selected-option-click-container"
        onClick={handleOpenAndClose}
        onKeyPress={handleOpenAndClose}
        role="button"
        tabIndex={0}
      >
        {children}
      </div>
      <CSSTransition
        classNames={cssTransitionClasses}
        in={showDropdown}
        timeout={200}
        unmountOnExit
      >
        {dropdownContents}
      </CSSTransition>
    </div>
  );
};

export default DropdownWrapper;
